import React, { useCallback, useState } from 'react';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import ProductCategoryModel from './models/ProductCategoryModel';
import ProductCategoryModal from './ProductCategoryModal';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { logAuditEvent } from '../../../utils/auditLogger';
import { buildProductCategorySavePayload, mapProductCategoryFromApi } from './utils/mapProductCategory';
import { toBooleanFromBit } from './utils/productCategoryHelpers';
import { validateProductCategory } from './productCategoryValidation';

function ProductCategoryGrid({
  CategoryData,
  fetchCategories,
  UserPermissions = {},
  moduleName = 'Product Category',
}) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [categoryModel, setCategoryModel] = useState(new ProductCategoryModel());
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const isRowActive = (row) => toBooleanFromBit(row?.IsActive, false);

  const closeModal = () => {
    setIsOpen(false);
    fetchCategories();
  };

  const rows = Array.isArray(CategoryData)
    ? CategoryData.filter((item) => {
        const search = filterText.toLowerCase();
        return (
          (item.CategoryName && item.CategoryName.toLowerCase().includes(search)) ||
          (item.Slug && item.Slug.toLowerCase().includes(search)) ||
          (item.ImageUrl && item.ImageUrl.toLowerCase().includes(search))
        );
      })
    : [];

  const loadCategoryDetail = async (categoryId) => {
    const response = await axios.get(`${getApiBaseUrl()}/ec-product-category/getById`, {
      params: { CategoryId: categoryId },
    });
    return mapProductCategoryFromApi(response.data?.data);
  };

  const openActionType = useCallback(
    async (row, name) => {
      const detail = row?.CategoryName ? `Category: ${row.CategoryName}` : '';
      logAuditEvent(moduleName, `${name} Click`, `${name} clicked. ${detail}`.trim());
      setLoadingDetail(true);
      try {
        const full = row?.CategoryId ? await loadCategoryDetail(row.CategoryId) : row;
        setCategoryModel(full || new ProductCategoryModel());
        setActionType(name);
        setIsOpen(true);
      } catch (err) {
        console.error(err);
        CreateAlert('Failed to load category details', 'Error', setAlert);
      } finally {
        setLoadingDetail(false);
      }
    },
    [moduleName]
  );

  const toggleActive = useCallback(
    async (row) => {
      if (UserPermissions.EditModule !== 1 || !row?.CategoryId) return;

      const nextActive = !isRowActive(row);
      setTogglingId(row.CategoryId);
      logAuditEvent(
        moduleName,
        'Toggle Active',
        `Category ${row.CategoryName} set to ${nextActive ? 'active' : 'inactive'}`
      );

      try {
        const payload = buildProductCategorySavePayload(
          { ...mapProductCategoryFromApi(row), IsActive: nextActive },
          getStorageValues('userId')
        );
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-category/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(
            result.message || `Category ${nextActive ? 'activated' : 'deactivated'} successfully!!`,
            'Success',
            setAlert
          );
          fetchCategories();
        } else {
          CreateAlert(result?.message || 'Failed to update category status!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update category status!!', 'Error', setAlert);
      } finally {
        setTogglingId(null);
      }
    },
    [UserPermissions.EditModule, fetchCategories, moduleName]
  );

  const onSubmit = async (e, data) => {
    if (e.target.name === 'Edit') {
      const payload = buildProductCategorySavePayload(data, getStorageValues('userId'));
      if (!validateProductCategory(payload, setAlert)) return;
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-category/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Category updated successfully!!', 'Success', setAlert);
          fetchCategories();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to update category!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update category!!', 'Error', setAlert);
      }
    } else if (e.target.name === 'Delete') {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-category/delete`, {
          CategoryId: categoryModel.CategoryId,
          UserId: getStorageValues('userId'),
        });
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Category deleted successfully!!', 'Success', setAlert);
          fetchCategories();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to delete category!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to delete category!!', 'Error', setAlert);
      }
    }
  };

  const columns = [
    {
      name: (
        <div className="titlename">
          <span>Category Name</span>
        </div>
      ),
      selector: (row) => row.CategoryName,
      cell: (row) => <span title={row.CategoryName}>{row.CategoryName}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Slug</span>
        </div>
      ),
      selector: (row) => row.Slug,
      cell: (row) => <span title={row.Slug}>{row.Slug}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Image URL</span>
        </div>
      ),
      selector: (row) => row.ImageUrl,
      cell: (row) => <span title={row.ImageUrl}>{row.ImageUrl || '-'}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Display Order</span>
        </div>
      ),
      selector: (row) => row.DisplayOrder,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '130px',
    },
    {
      name: (
        <div className="titlename">
          <span>Active</span>
        </div>
      ),
      cell: (row) => (
        <div className="status-toggle modal-status">
          <input
            type="checkbox"
            id={`category-active-${row.CategoryId}`}
            className="check"
            checked={isRowActive(row)}
            disabled={UserPermissions.EditModule !== 1 || togglingId === row.CategoryId}
            onChange={() => toggleActive(row)}
          />
          <label htmlFor={`category-active-${row.CategoryId}`} className="checktoggle" />
        </div>
      ),
      width: '100px',
    },
  ];

  if (UserPermissions.ViewModule === 1 || UserPermissions.EditModule === 1 || UserPermissions.DeleteModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {UserPermissions.ViewModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'View')}>
              <Icons.Eye className="action-eye" />
            </Link>
          ) : null}
          {UserPermissions.EditModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'Edit')}>
              <Icons.Edit className="feather-edit" />
            </Link>
          ) : null}
          {UserPermissions.DeleteModule === 1 ? (
            <Link className="confirm-text p-2" to="" onClick={() => openActionType(row, 'Delete')}>
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          ) : null}
        </div>
      ),
      center: 'true',
      width: '150px',
    });
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search-category"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="CategoryName"
            sortIcon={<SortArrow />}
            persistTableHead
            progressPending={loadingDetail}
          />
        </div>
      </div>
      <ProductCategoryModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={categoryModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductCategoryGrid;
